import React from 'react';
import './StormDash.css';
import { launchGameWithMobileOptimization } from '../utils/mobileGameLauncher';

const StormDash = () => {
  return (
    <div className="main-content">
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid-pattern"></div>
          <div className="hero-orbs">
            <div className="hero-orb hero-orb-1"></div>
            <div className="hero-orb hero-orb-2"></div>
            <div className="hero-orbs">
              <div className="hero-orb hero-orb-1"></div>
              <div className="hero-orb hero-orb-2"></div>
              <div className="hero-orb hero-orb-3"></div>
            </div>
          </div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">Storm Dash</h1>
          <p className="hero-subtitle">Master the art of survival in this intense wave defense experience</p>
        </div>
      </section>

      <section className="section">
        <div className="floating-stars">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>
        <div className="container">
          <div className="game-overview">
            <div className="game-description">
              <h2>Game Overview</h2>
              <p>
                <strong>Storm Dash</strong> is an intense wave defense game where survival is everything and style is your weapon. 
                Face endless waves of enemies while crafting your unique playstyle through a vast arsenal of abilities and equipment. 
                Every run is different, every choice matters, and only the most adaptable survivors will see another dawn.
              </p>
              <p>
                Customize your character with dozens of abilities, experiment with powerful synergies, and discover the perfect build 
                that matches your playstyle. Whether you prefer aggressive rushing, tactical positioning, or defensive mastery - 
                the choice is yours, and the storm is waiting.
              </p>
              <div className="beta-notice">
                <p><strong>⚠️ Early Beta Notice:</strong> Storm Dash is currently in early beta development. Some features may not work as expected, 
                and we're actively working to improve the experience. Your feedback helps us make the game better!</p>
              </div>
            </div>

            <div className="game-preview-card">
              <div className="preview-image">
                <div className="game-logo-container">
                  <img src="/blueteddyfloat.png" alt="Storm Dash Logo" className="storm-dash-logo" />
                  <div className="logo-glow"></div>
                </div>
              </div>
              <div className="preview-info">
                <h3>Play Storm Dash</h3>
                <p>Experience the thrill of our latest game</p>
                <button className="btn btn-accent" onClick={() => {
                  const url = window.location.origin + '/storm-dash-preview/index.html';
                  launchGameWithMobileOptimization(url);
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 3l14 9-14 9V3z"/>
                  </svg>
                  Launch Game
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="floating-stars">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>
        <div className="container">
          <h2 className="section-title">Game Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <h3>Endless Wave Survival</h3>
              <p>Face increasingly challenging waves of enemies in an intense survival experience. 
                 Each wave brings new threats and tests your adaptability.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <h3>Ability Customization</h3>
              <p>Equip and combine dozens of unique abilities to create your perfect build. 
                 Discover powerful synergies and forge your own playstyle.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                  <path d="M6 2v2"/>
                  <path d="M6 20v2"/>
                  <path d="M6 15H4.5a2.5 2.5 0 0 0 0 5H6"/>
                  <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                  <path d="M18 2v2"/>
                  <path d="M18 20v2"/>
                  <path d="M18 15h1.5a2.5 2.5 0 0 1 0 5H18"/>
                </svg>
              </div>
              <h3>Multiple Playstyles</h3>
              <p>Whether you prefer aggressive assault, tactical positioning, or defensive mastery - 
                 find the approach that suits you best and master it.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-7.73A2 2 0 1 1 19 12H2"/>
                </svg>
              </div>
              <h3>Dynamic Challenges</h3>
              <p>Every run presents new challenges and opportunities. Adapt your strategy 
                 as conditions change and enemies evolve.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                  <line x1="9" y1="9" x2="9.01" y2="9"/>
                  <line x1="15" y1="9" x2="15.01" y2="9"/>
                </svg>
              </div>
              <h3>Character Progression</h3>
              <p>Unlock new abilities, upgrade your arsenal, and become stronger with each run. 
                 Your survival skills will be put to the ultimate test.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18V5l12-2v13"/>
                  <circle cx="6" cy="18" r="3"/>
                  <circle cx="18" cy="16" r="3"/>
                </svg>
              </div>
              <h3>Atmospheric Experience</h3>
              <p>Immerse yourself in a world where every sound, visual effect, and animation 
                 contributes to the intense survival atmosphere.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="floating-stars">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>
        <div className="container">
          <h2 className="section-title">How to Play</h2>
          <div className="how-to-play">
            <div className="instruction">
              <div className="instruction-number">1</div>
              <div className="instruction-content">
                <h3>Choose Your Abilities</h3>
                <p>Before each run, equip abilities that match your preferred playstyle. 
                   Experiment with different combinations to discover powerful synergies.</p>
              </div>
            </div>
            <div className="instruction">
              <div className="instruction-number">2</div>
              <div className="instruction-content">
                <h3>Survive the Waves</h3>
                <p>Face increasingly difficult waves of enemies. Use movement, timing, 
                   and your equipped abilities to stay alive as long as possible.</p>
              </div>
            </div>
            <div className="instruction">
              <div className="instruction-number">3</div>
              <div className="instruction-content">
                <h3>Adapt Your Strategy</h3>
                <p>Each wave brings new challenges. Adjust your tactics, positioning, 
                   and ability usage to counter different enemy types and patterns.</p>
              </div>
            </div>
            <div className="instruction">
              <div className="instruction-number">4</div>
              <div className="instruction-content">
                <h3>Master Your Style</h3>
                <p>The longer you survive, the more you'll understand your build. 
                   Refine your playstyle and push for ever-higher survival records.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="floating-stars">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>
        <div className="container">
          <h2 className="section-title">Development Status</h2>
          <div className="development-status">
            <div className="status-item">
              <div className="status-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22,4 12,14.01 9,11.01"/>
                </svg>
              </div>
              <h3>Core Mechanics</h3>
              <p>Complete</p>
            </div>
            <div className="status-item">
              <div className="status-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <h3>Visual Assets</h3>
              <p>In Progress</p>
            </div>
            <div className="status-item">
              <div className="status-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18V5l12-2v13"/>
                  <circle cx="6" cy="18" r="3"/>
                  <circle cx="18" cy="16" r="3"/>
                </svg>
              </div>
              <h3>Audio Design</h3>
              <p>Planning</p>
            </div>
            <div className="status-item">
              <div className="status-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
              </div>
              <h3>Testing</h3>
              <p>Upcoming</p>
            </div>
          </div>
          <div className="status-note">
            <p>We're working hard to bring Storm Dash to life! Follow our social media for
               development updates and behind-the-scenes content.</p>
          </div>
          
          <div className="development-approach">
            <h3>Built with GDevelop</h3>
            <p>
              Storm Dash was created using GDevelop, a free and open-source game development platform that allows 
              creators to build games without writing code. This approach enabled us to focus on crafting the perfect 
              wave defense experience, balancing challenging gameplay with customizable character builds, while leveraging 
              GDevelop's powerful visual programming tools to create smooth combat mechanics and dynamic enemy AI.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StormDash;
